import { Tooltip } from '@mui/material';
import { Handle, Position } from '@xyflow/react';
import { UPDATE_SOURCE_INFO_REQUEST } from 'js/constants/actionTypes';
import {
  CONNECTION_CATEGORY_TEMPLATIZED_API,
  CONNECTION_CATEGORY_TEMPLATIZED_API_MULTI,
} from 'js/constants/connectionCategories';
import { ReactComponent as ClockIcon } from 'new/components/icons/fi-rr-clock.svg';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import React, { useEffect, useMemo, useState } from 'react';
import CronScheduleDialog from 'js/components/pipelines/cards/components/ExpandActions/CronScheduleDialog';
import { useUpdateConfig } from 'js/components/pipelines/cards/components/ExpandActions/hooks/useExpandActionsEffects';
import { renderNextRunTime } from 'js/components/pipelines/cards/components/ExpandActions/utils/renderNextRunTime';
import styles from '../FlowNodes.module.scss';
import nodeStyles from './ScheduleNode.module.scss';
import { FlowTriggerHelper } from 'js/components/data_sources/add_ds_form/MonitoringInterval/FlowTriggerHelper';

interface CronJobNodeProps {
  data: {
    layoutDirection?: 'LR' | 'TB';
    connectionCategory?: string | null;
    resourceId?: string;
    id?: string;
    flow_node_id?: number;
    source_config?: Record<string, unknown>;
    template_config?: Record<string, unknown>;
    flow_triggers?: Array<{ triggering_resource_type?: string }>;
    flow_type?: string;
    status?: string;
    [key: string]: any;
  };
}

export const CronJobNode: React.FC<CronJobNodeProps> = ({ data }) => {
  const layoutDirection = data.layoutDirection ?? 'LR';
  const isTB = layoutDirection === 'TB';
  const sourceHandleId = isTB ? 'bottom' : 'right';
  // In TB the node always sits above the source and emits from its bottom
  // edge (solo: straight vertical drop; paired: shallow V converging at
  // the source top). In LR it sits to the left of the source and emits
  // from the right edge.
  const handlePos = isTB ? Position.Bottom : Position.Right;

  const connectionCategory = data.connectionCategory ?? '';
  const resourceId = String(data.resourceId ?? data.id ?? '');

  const config = useMemo(() => {
    if (
      [CONNECTION_CATEGORY_TEMPLATIZED_API, CONNECTION_CATEGORY_TEMPLATIZED_API_MULTI].includes(
        connectionCategory,
      ) &&
      data?.template_config?.['start.cron']
    ) {
      return data.template_config;
    }
    return data?.source_config;
  }, [connectionCategory, data]);

  const cronExpression = config?.['start.cron'] as string | undefined;

  const [showCronModal, setShowCronModal] = useState(false);
  const [newConfig, setNewConfig] = useState(config || {});
  const [cronAnchorEl, setCronAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!showCronModal) {
      setNewConfig(config || {});
    }
  }, [config, showCronModal]);

  const { handleUpdateConfig } = useUpdateConfig(resourceId);
  const isUpdateSourceLoading = useIsLoading(UPDATE_SOURCE_INFO_REQUEST);

  const isCronOnSinkCompletion = FlowTriggerHelper.getInboundTriggers(data).some(
    (trigger) => trigger.triggering_resource_type === 'data_sink',
  );

  const isCronOnSourceCompletion =
    // Only triggers that START this source; its own outbound ones (it triggers another
    // flow) must not label it "On Source Completion" (NEX-22548).
    FlowTriggerHelper.getInboundTriggers(data).some(
      (trigger) => trigger.triggering_resource_type === 'data_source',
    );

  const isExternalTriggerCompatibleSource = data?.['adaptive_flow'] === true;

  const handleRunCronJob = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Previously bailed out entirely when no cron was set yet — meaning a
    // fresh CronJob node (e.g. BYO's source_config never carries `start.cron`
    // — stripped as a vestige field on purpose, see persistence.ts) could
    // never open the dialog to set one up in the first place: "Edit" only
    // worked once a schedule already existed. Confirmed bug, fixed
    // 2026-08-20 — always open; CronScheduleDialog already handles an empty
    // `config` (that's the same state any brand-new flow of any type starts
    // from before its first save).
    setNewConfig(config || {});
    setShowCronModal(true);
    setCronAnchorEl(e.currentTarget as HTMLElement);
  };

  const handleClosePopover = () => {
    setShowCronModal(false);
    setCronAnchorEl(null);
  };

  const handleUpdateConfigWithUI = () => {
    handleUpdateConfig(newConfig, data, connectionCategory, () => {
      setShowCronModal(false);
      setCronAnchorEl(null);
    });
  };

  const handleConfigChange = (cfg: any, fieldName: string, value: any) => {
    setNewConfig({ ...cfg, [fieldName]: value });
  };

  const nextRunText = cronExpression
    ? isCronOnSinkCompletion
      ? `On Destination Completion\n${renderNextRunTime(cronExpression)}`
      : isCronOnSourceCompletion
        ? `On Source Completion\n${renderNextRunTime(cronExpression)}`
        : renderNextRunTime(cronExpression)
    : 'Not scheduled';

  return (
    <div className={nodeStyles.scheduleNode} data-testid="cron-job-node">
      <Handle
        type="source"
        position={handlePos}
        isConnectable={false}
        className={styles.handle}
        id={sourceHandleId}
      />
      <div className={nodeStyles.nodeContent}>
        <Tooltip title="Edit cron schedule" placement="top" arrow>
          <div className={nodeStyles.iconCircle} onClick={handleRunCronJob}>
            <ClockIcon />
          </div>
        </Tooltip>
        <CronScheduleDialog
          open={showCronModal}
          onClose={handleClosePopover}
          config={newConfig}
          onConfigChanged={handleConfigChange}
          onUpdate={handleUpdateConfigWithUI}
          anchorEl={cronAnchorEl}
          isLoading={isUpdateSourceLoading}
          source={data}
          showExternalTrigger={isExternalTriggerCompatibleSource}
        />
        <div
          className={nodeStyles.labelBadge}
          onClick={handleRunCronJob}
          style={{ cursor: 'pointer' }}
        >
          CRON JOB
        </div>
        <div
          className={nodeStyles.subText}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleRunCronJob(e);
          }}
        >
          <div>Next Run</div>
          {nextRunText.split('\n').map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
      </div>
    </div>
  );
};
